import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export function luhnValidator(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value ?? '').toString().replace(/\s/g, '');
  if (!raw) {
    return null;
  }
  if (!/^\d+$/.test(raw)) {
    return { luhn: true };
  }
  if (raw.length < 13 || raw.length > 19) {
    return { luhn: true };
  }
  let sum = 0;
  let alt = false;
  for (let i = raw.length - 1; i >= 0; i--) {
    let n = parseInt(raw[i], 10);
    if (alt) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0 ? null : { luhn: true };
}

export function notExpiredValidator(group: AbstractControl): ValidationErrors | null {
  const fg = group as FormGroup;
  const month = fg.get('expirationMonth')?.value;
  const year = fg.get('expirationYear')?.value;
  if (month == null || month === '' || year == null || year === '') {
    return null;
  }
  const m = typeof month === 'string' ? parseInt(month, 10) : Number(month);
  const y = typeof year === 'string' ? parseInt(year, 10) : Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;
  if (y < cy || (y === cy && m < cm)) {
    return { expired: true };
  }
  return null;
}

export function cvvLengthValidator(length: 3 | 4): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value ?? '').toString();
    if (!v) {
      return null;
    }
    if (!/^\d+$/.test(v)) {
      return { cvvLength: true };
    }
    return v.length === length ? null : { cvvLength: true };
  };
}

const US_ZIP = /^\d{5}(-\d{4})?$/;
/** Canadian postal code (allows optional space). */
const CA_POSTAL = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

export function zipForCountryValidator(control: AbstractControl): ValidationErrors | null {
  const parent = control.parent as FormGroup | null;
  if (!parent) {
    return null;
  }
  const country = (parent.get('country')?.value ?? '').toString().toUpperCase();
  const zip = (control.value ?? '').toString().trim();
  if (!zip) {
    return null;
  }
  if (country === 'US' && !US_ZIP.test(zip)) {
    return { zipPattern: true };
  }
  if (country === 'CA' && !CA_POSTAL.test(zip)) {
    return { zipPattern: true };
  }
  if (country && country !== 'US' && country !== 'CA') {
    return zip.length <= 20 ? null : { zipPattern: true };
  }
  return null;
}

export function stateForCountryValidator(control: AbstractControl): ValidationErrors | null {
  const parent = control.parent as FormGroup | null;
  if (!parent) {
    return null;
  }
  const country = (parent.get('country')?.value ?? '').toString().toUpperCase();
  const state = (control.value ?? '').toString().trim();
  if (!state) {
    return null;
  }
  if ((country === 'US' || country === 'CA') && !/^[A-Za-z]{2}$/.test(state)) {
    return { stateCode: true };
  }
  return null;
}

export function amountMaxValidator(max: number | undefined): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (max == null || !Number.isFinite(max)) {
      return null;
    }
    const v = control.value;
    if (v === '' || v == null) {
      return null;
    }
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    if (!Number.isFinite(n)) {
      return null;
    }
    return n > max ? { maxAmount: { max, actual: n } } : null;
  };
}
